import React, { useEffect, useState } from "react";
import { Col, Row, Checkbox, List, Card, Form, Flex, Input, Select, Button } from "antd";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useParams } from "react-router-dom";
import { addQuestionAnswerAction, getQuestionAnswerAction } from "../../../../../../store/pages/plans/actions";
import { RootState } from "../../../../../../store";
import { MyBreadcrumb, MyButton, MyText } from "../../../../../../components";
import { selectStore } from "../../../../../../store/selector";
import { useTranslation } from "react-i18next";
import { DeleteOutlined, LeftOutlined, RightOutlined } from "@ant-design/icons";
import { TextEditor } from "../../../../../../components/common/TextEditor";
import { Color } from "../../../../../../themes";
import { ROUTE_PATH } from "../../../../../../constants";

const FORM_ITEM_NAME = {
  ID: 'id',
  TEST_ID: 'test_id',
  NAME: 'name',
  DIFFICULTY: 'difficulty',
  ANSWERS: 'answers',
  QUESTIONS: 'questions',
  IS_CORRECT: 'is_correct',
}

let arrayInitialState = {
  [FORM_ITEM_NAME.TEST_ID]: null,
  [FORM_ITEM_NAME.QUESTIONS]: []
}

export const TestView = () => {
  const { t } = useTranslation("common")
  const [testForm] = Form.useForm()
  const location = useLocation()

  const difficult = [t('common:easy'), t('common:average'), t('common:hard')]
  const dispatch = useDispatch()
  const { ctpId, lessonId, testId } = useParams()

  const [activeKey, setActiveKey] = useState<number>(0);
  const [isEditMode, setIsEditMode] = useState<boolean>(false)

  useEffect(() => {
    dispatch(getQuestionAnswerAction(testId))
  }, [])

  const { plans: { oneTest: oneTestState } }: RootState = useSelector(selectStore)

  useEffect(() => {
    if (oneTestState.data) {
      testForm.setFieldsValue({
        [FORM_ITEM_NAME.TEST_ID]: testId,
        [FORM_ITEM_NAME.QUESTIONS]: oneTestState.data?.questions
      })
    }
  }, [oneTestState.data])
  const handleSave = () => {
    setIsEditMode(false)
    dispatch(addQuestionAnswerAction(testForm.getFieldsValue()))
  }

  const isDisable = (location.pathname.split('/')[1] !== ROUTE_PATH.LIBRARY.split('/')[1])

  return (
    <>
      <MyBreadcrumb title={t('back')} isBack />

      <Row justify='center'>
        <Col span={20}>
          <Row gutter={[16, 16]}>
            <Col span={18}>
              <Card title={(
                <Row align='middle' justify='space-between'>
                  <Col flex={'200px'}>
                    {
                      activeKey != 0 &&
                      <Flex align='center' justify='start' gap={8} style={{ cursor: 'pointer' }}
                        onClick={() => {
                          setActiveKey(prevState => prevState - 1)
                          setIsEditMode(false)
                        }}>
                        <LeftOutlined />
                        <MyText>
                          {t('prev')}
                        </MyText>
                      </Flex>
                    }

                  </Col>

                  <Col>
                    <MyText>
                      {t('question')} №{activeKey + 1}
                    </MyText>
                  </Col>

                  <Col flex={'200px'}>
                    {
                      (activeKey + 1) !== oneTestState?.data?.questions?.length &&
                      <Flex align='center' justify='end' gap={8} style={{ cursor: 'pointer' }}
                        onClick={() => {
                          setActiveKey(prevState => prevState + 1)
                          setIsEditMode(false)
                        }}>
                        <MyText>
                          {t('next')}
                        </MyText>
                        <RightOutlined />
                      </Flex>
                    }

                  </Col>
                </Row>
              )} loading={oneTestState.loading}>

                <Row>
                  <Col span={24}>
                    <Row gutter={[24, 24]}>
                      <Form
                        autoComplete="off"
                        form={testForm}
                        name="form"
                        initialValues={arrayInitialState}
                        disabled={!isEditMode}
                      >
                        <Row gutter={[0, 16]}>
                          <Col span={24} style={{ display: 'none' }}>
                            <Form.Item name={FORM_ITEM_NAME.TEST_ID} hidden>
                              <Input hidden />
                            </Form.Item>
                          </Col>
                          <Col span={24}>
                            <Form.List name={FORM_ITEM_NAME.QUESTIONS}>
                              {
                                (fields, operation, meta) => {
                                  return (
                                    fields.map((item, index) => {
                                      let currentItem = testForm.getFieldsValue()?.[FORM_ITEM_NAME.QUESTIONS]?.[item.key]

                                      return (
                                        item.name === activeKey && (
                                          <Form.Item key={item.key}>
                                            <Row gutter={[16, 16]}>
                                              <Col span={24} style={{ display: 'none' }}>
                                                <Form.Item name={[item.name, FORM_ITEM_NAME.ID]} hidden>
                                                  <Input hidden />
                                                </Form.Item>
                                              </Col>
                                              <Col span={24}>
                                                <Form.Item labelCol={{ span: 24 }} wrapperCol={{ span: 24 }}
                                                  name={[item.name, FORM_ITEM_NAME.DIFFICULTY]}
                                                  label={isEditMode ? t('questionLevel') : ''}>
                                                  {
                                                    isEditMode ?
                                                      <Select
                                                        options={difficult.map((dif, difIndex) => ({
                                                          key: difIndex + 1,
                                                          label: dif,
                                                          value: difIndex + 1
                                                        }))} />
                                                      :
                                                      <MyText size={16}>
                                                        {t('questionLevel')}: {difficult[currentItem?.difficulty - 1]}
                                                      </MyText>
                                                  }

                                                </Form.Item>
                                              </Col>
                                              <Col span={24}>
                                                <Form.Item labelCol={{ span: 24 }} wrapperCol={{ span: 24 }}
                                                  name={[item.name, FORM_ITEM_NAME.NAME]}
                                                  label={isEditMode ? t('enterQuestion') : ''}>
                                                  <TextEditor
                                                    value={currentItem?.[FORM_ITEM_NAME.NAME]}
                                                    disabled={!isEditMode}
                                                  />
                                                </Form.Item>
                                              </Col>
                                              <Col span={24}>
                                                <Form.Item labelCol={{ span: 24 }} wrapperCol={{ span: 24 }}
                                                  label={t('answers')}
                                                  name={[item.name, FORM_ITEM_NAME.ANSWERS]}>
                                                  <Form.List name={[item.name, FORM_ITEM_NAME.ANSWERS]}>
                                                    {
                                                      (subFields, subOpt) => {

                                                        return (
                                                          <Row gutter={[16, 16]}>
                                                            {subFields.map((subField) => {
                                                              let currentInnerItem = currentItem?.[FORM_ITEM_NAME.ANSWERS]?.[subField.name]

                                                              return (
                                                                <Col key={subField.key} span={24}>
                                                                  <Row justify='space-between' align='middle'>
                                                                    <Col span={24}>
                                                                      <Card
                                                                        style={{ backgroundColor: isEditMode ? Color.lightHover : 'transparent' }}
                                                                        styles={{
                                                                          body: {
                                                                            padding: 4
                                                                          },
                                                                          header: {
                                                                            padding: 4,
                                                                            minHeight: 'auto'
                                                                          }
                                                                        }}
                                                                        title={(
                                                                          <Row align='middle' justify='space-between'>
                                                                            <Col>
                                                                              <Row gutter={[8, 8]} align='middle'>
                                                                                <Col>
                                                                                  {t('answer')}
                                                                                </Col>

                                                                                <Col>
                                                                                  <Form.Item noStyle
                                                                                    name={[subField.name, FORM_ITEM_NAME.IS_CORRECT]}
                                                                                    valuePropName="checked"
                                                                                    initialValue={false}
                                                                                    key={subField.key + 'checkbox'}
                                                                                    required={true}>
                                                                                    <Checkbox />
                                                                                  </Form.Item>
                                                                                </Col>
                                                                              </Row>
                                                                            </Col>

                                                                            <Col>
                                                                              {
                                                                                isEditMode && (
                                                                                  <Button type='text'
                                                                                    icon={<DeleteOutlined />}
                                                                                    onClick={() => {
                                                                                      subOpt.remove(subField.name);
                                                                                    }} />
                                                                                )
                                                                              }
                                                                            </Col>
                                                                          </Row>
                                                                        )}>

                                                                        <Row align='middle' gutter={[8, 8]}>
                                                                          <Col flex={1}>
                                                                            <Form.Item noStyle
                                                                              name={[subField.name, FORM_ITEM_NAME.NAME]}
                                                                              required={true}
                                                                              key={subField.key + 'input'}>
                                                                              <TextEditor
                                                                                value={currentInnerItem?.[FORM_ITEM_NAME.NAME]}
                                                                                disabled={!isEditMode}
                                                                              />
                                                                            </Form.Item>
                                                                          </Col>
                                                                        </Row>
                                                                      </Card>
                                                                    </Col>
                                                                  </Row>
                                                                </Col>
                                                              )
                                                            })}
                                                            {
                                                              isEditMode &&
                                                              (
                                                                subFields.length <= 4 &&
                                                                <Col span={8}>
                                                                  <Button type="primary" onClick={() => {
                                                                    subOpt.add()
                                                                  }} block>
                                                                    {t('addAnswerVariant')}
                                                                  </Button>
                                                                </Col>
                                                              )
                                                            }
                                                          </Row>
                                                        )
                                                      }
                                                    }
                                                  </Form.List>
                                                </Form.Item>
                                              </Col>
                                            </Row>
                                          </Form.Item>
                                        )
                                      )
                                    })
                                  )
                                }
                              }
                            </Form.List>
                          </Col>
                        </Row>
                      </Form>
                    </Row>
                  </Col>
                  {
                    isDisable &&
                    <Col span={24}>
                      <Row justify='center' gutter={[16, 16]}>
                        <Col>
                          <MyButton type='primary' ghost
                            onClick={() => {
                              setIsEditMode(prevState => !prevState)
                            }}>{isEditMode ? t('preview') : t('edit')}</MyButton>
                        </Col>

                        {
                          isEditMode &&
                          <Col>
                            <MyButton title={t('save')} type='primary' onClick={handleSave} />
                          </Col>
                        }

                      </Row>
                    </Col>
                  }
                </Row>
              </Card>
            </Col>

            <Col span={6}>
              <Card loading={oneTestState.loading}>
                <Row gutter={[16, 16]}>
                  <Col span={24}>
                    <MyText color={Color.titleText} weight={500} size={16}>
                      {t('totalQuestions', { count: oneTestState?.data?.questions?.length })}
                    </MyText>
                  </Col>

                  <Col span={24}>
                    <Flex wrap='wrap' gap={4}>
                      {
                        oneTestState?.data?.questions?.map((item: any, index: any) => (
                          <Flex style={{
                            minWidth: 40,
                            minHeight: 40,
                            width: 40,
                            height: 40,
                            border: `1px solid ${activeKey == index ? Color.hover : Color.border}`,
                            borderRadius: 8,
                            cursor: 'pointer',
                            background: activeKey == index ? Color.hover : 'transparent'
                          }} key={index} align='center' justify='center' onClick={() => {
                            setActiveKey(index)
                            setIsEditMode(false)
                          }}>
                            <MyText color={activeKey == index ? '#fff' : Color.subtitle}>
                              {index + 1}
                            </MyText>
                          </Flex>
                        ))
                      }
                    </Flex>
                  </Col>
                </Row>
              </Card>
            </Col>
          </Row>
        </Col>
      </Row>
    </>
  )
}